import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { cn } from 'utils/cn'

export interface SearchFieldProps {
    /** `outlined`: a visible 1px border and no shadow. */
    variant?: 'primary' | 'secondary' | 'outlined'
    label?: string
    placeholder?: string
    description?: string
    errorMessage?: string
    isInvalid?: boolean
    disabled?: boolean
    showLabel?: boolean
    showDescription?: boolean
    value?: string
    defaultValue?: string
    onChange?: (value: string) => void
    onClear?: () => void
    onSubmit?: (value: string) => void
    /** Accessible name for the input when there is no visible `label`. */
    ariaLabel?: string
    style?: React.CSSProperties
}

export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
    (
        {
            variant = 'primary',
            label,
            placeholder = 'Search...',
            description,
            errorMessage,
            isInvalid = false,
            disabled = false,
            showLabel = true,
            showDescription = true,
            value: controlledValue,
            defaultValue = '',
            onChange,
            onClear,
            onSubmit,
            ariaLabel,
            style,
        },
        ref
    ) => {
        const isSecondary = variant === 'secondary'
        const isOutlined = variant === 'outlined'
        const [internalValue, setInternalValue] = useState(defaultValue)
        const inputRef = useRef<HTMLInputElement>(null)
        useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

        const value = controlledValue !== undefined ? controlledValue : internalValue
        const hasValue = value.length > 0

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const v = e.target.value
            if (controlledValue === undefined) setInternalValue(v)
            onChange?.(v)
        }

        const handleClear = () => {
            if (controlledValue === undefined) setInternalValue('')
            onChange?.('')
            onClear?.()
            inputRef.current?.focus()
        }

        const handleKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') onSubmit?.(value)
        }

        return (
            <div className={cn('flex min-w-[116px] flex-col gap-1', disabled && 'opacity-50')} style={style}>
                {showLabel && label && (
                    <label
                        className={cn(
                            'inline-flex flex-wrap items-center gap-1 whitespace-nowrap pr-2 text-sm font-medium',
                            isInvalid ? 'text-danger' : 'text-foreground'
                        )}
                    >
                        {label}
                    </label>
                )}

                <div className="relative flex items-center">
                    <div
                        className={cn(
                            'relative flex h-9 w-full items-center gap-1.5 overflow-hidden rounded-xl px-3 py-2 transition-[box-shadow,background] duration-150',
                            isSecondary
                                ? 'border-none bg-default'
                                : isOutlined
                                  ? 'border border-border bg-surface'
                                  : 'border-none bg-surface shadow-field not-focus-within:hover:bg-surface-secondary',
                            isOutlined
                                ? isInvalid
                                    ? 'border-danger ring-1 ring-danger'
                                    : 'focus-within:border-accent focus-within:ring-1 focus-within:ring-accent'
                                : isInvalid
                                  ? 'ring-2 ring-danger'
                                  : 'focus-within:ring-2 focus-within:ring-accent'
                        )}
                    >
                        <span className="flex shrink-0 items-center text-foreground-muted">
                            <Search size={16} strokeWidth={1.5} />
                        </span>
                        <input
                            ref={inputRef}
                            type="search"
                            aria-label={ariaLabel}
                            value={value}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            placeholder={placeholder}
                            disabled={disabled}
                            className="min-w-0 flex-1 border-none bg-transparent p-0 text-sm leading-5 text-foreground outline-none placeholder:text-foreground-muted"
                        />
                    </div>
                    {hasValue && !disabled && (
                        <button
                            type="button"
                            aria-label="Clear search"
                            onClick={handleClear}
                            className="absolute right-2 top-1/2 flex shrink-0 -translate-y-1/2 cursor-pointer items-center rounded-xl border-none bg-default p-[3.333px] text-default-foreground"
                        >
                            <X size={16} strokeWidth={1.5} />
                        </button>
                    )}
                </div>

                {showDescription &&
                    (isInvalid && errorMessage ? (
                        <span className="text-sm text-danger">{errorMessage}</span>
                    ) : description ? (
                        <span className="text-sm text-foreground-muted">{description}</span>
                    ) : null)}
            </div>
        )
    }
)

SearchField.displayName = 'SearchField'
